import { useCallback, useState, useRef } from 'react';

import responsePreviousWorkButtonClick from '@hh.ru/analytics-js-events/build/xhh/employer/responses/resume_card/response_previous_work_button_click';
import { Loader } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import defaultRequestErrorHandler from 'src/api/notifications/defaultRequestErrorHandler';
import defaultError from 'src/components/Notifications/DefaultError';
import { useNotification } from 'src/components/Notifications/Provider';
import StaticCollapseTrigger from 'src/components/ResumeCard/Body/Collapse/Trigger/Static';
import useVacancyId from 'src/components/ResumeCard/hooks/useVacancyId';
import translation from 'src/components/translation';
import useToggleState from 'src/hooks/useToggleState';
import { Experience } from 'src/models/resume/resumeCommon.types';
import { ResumeCardData } from 'src/models/resumeCard';
import fetcher from 'src/utils/fetcher';

const REMOTE = '/shards/resume_experience';

declare global {
    interface FetcherGetApi {
        [REMOTE]: {
            queryParams: { hash: string };
            response: { experience: Array<Experience> };
        };
    }
}

const TrlKeys = {
    expand: 'resumeCard.field.lastExperience.trigger',
    collapse: 'resumeCard.trigger.collapse',
};

interface Props {
    resume: ResumeCardData;
    lastExperienceId: number;
    isGifted: boolean;
    isNew: boolean;
    markAsRead?: () => void;
}

const LastExperience: TranslatedComponent<Props> = ({
    resume,
    lastExperienceId,
    isGifted,
    isNew,
    markAsRead,
    trls,
}) => {
    const { addNotification } = useNotification();

    const lastExperience = resume.shortExperience?.[0];

    const [isLoading, setLoading] = useState(false);
    const [content, setContent] = useState<string | null>(null);
    const [expanded, toggleExpanded, setExpanded] = useToggleState(false);
    const abortController = useRef<AbortController | null>(null);

    const loadExperience = useCallback(
        async (hash: string, lastExperienceId: number, abortController: AbortController | null) => {
            setLoading(true);
            let data;
            try {
                data = await fetcher.get(REMOTE, { params: { hash }, signal: abortController?.signal });
            } catch (error) {
                if (fetcher.isCancel(error)) {
                    return;
                }
                defaultRequestErrorHandler(error, addNotification);
                setExpanded(false);
                return;
            } finally {
                setLoading(false);
            }
            const experience = data.experience.find((experience) => experience.id === lastExperienceId);
            if (!experience) {
                addNotification(defaultError);
                setExpanded(false);
                return;
            }
            setContent(experience.description);
        },
        [addNotification, setExpanded]
    );

    const vacancyId = useVacancyId();
    const handleClick = useCallback(() => {
        if (!lastExperience) {
            return;
        }
        toggleExpanded();
        if (!expanded && !content) {
            const controller = new AbortController();
            abortController.current = controller;
            void loadExperience(resume._attributes.hash, lastExperience.id, abortController.current);
        }
        isNew && markAsRead?.();
        responsePreviousWorkButtonClick({
            vacancyId: vacancyId?.toString() || '',
            gifted: isGifted,
            topicId: resume.topicId,
        });
    }, [
        lastExperience,
        toggleExpanded,
        expanded,
        content,
        isNew,
        markAsRead,
        vacancyId,
        isGifted,
        resume.topicId,
        resume._attributes.hash,
        loadExperience,
    ]);

    if (isLoading) {
        return <Loader size={16} />;
    }

    return (
        <>
            {expanded && content && (
                <div data-hh-last-experience-id={lastExperienceId}>
                    <div className="output-lastexp__text">{content}</div>
                </div>
            )}
            <StaticCollapseTrigger
                text={trls[expanded ? TrlKeys.collapse : TrlKeys.expand]}
                expanded={expanded}
                onToggle={handleClick}
                dataQa="last-experience-link"
            />
        </>
    );
};

export default translation(LastExperience);
